/**
 * バナーレイアウトエディタの部品 (傾き・商品画像)。キャンバス上のフローティング操作列 (BannerLayoutElementToolbar) と
 * 背景の分割 (BannerLayoutSplitButton) のポップオーバーから使う。
 * (旧 BannerLayoutInspector (右パネル) は §21 (2026-10-19) で廃止し、ここに部品だけ残した)
 */
import { productImageIndex, withImageIndex } from '../../../features/bannerLayout/productImages';
import { BANNER_LAYOUT_MAX_ROTATION, type BannerLayoutElement } from '../../../features/bannerLayout/types';
import styles from './BannerLayout.module.css';

export type InspectorAsset = { id: string; thumbUrl: string; name?: string };

const clampDeg = (v: number) => Math.max(-BANNER_LAYOUT_MAX_ROTATION, Math.min(BANNER_LAYOUT_MAX_ROTATION, Math.round(v)));

/** 傾き (-45〜45 度・1度刻み)。数値入力 + スライダー + 「0に戻す」。背景の分割線の傾き (BannerLayoutSplitButton) にも使う */
export function RotationControl({ value, onChange, label = '傾き' }: { value: number; onChange: (deg: number) => void; label?: string }) {
  return (
    <div className={styles.field} data-testid="banner-layout-rotation">
      <span>{label}</span>
      <div className={styles.rotationRow}>
        <input
          type="range"
          min={-BANNER_LAYOUT_MAX_ROTATION}
          max={BANNER_LAYOUT_MAX_ROTATION}
          step={1}
          value={value}
          onChange={(e) => onChange(clampDeg(Number(e.target.value)))}
          aria-label={`${label} (スライダー)`}
        />
        <input
          type="number"
          className={styles.rotationNumber}
          min={-BANNER_LAYOUT_MAX_ROTATION}
          max={BANNER_LAYOUT_MAX_ROTATION}
          step={1}
          value={value}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(clampDeg(v));
          }}
          aria-label={`${label} (度)`}
        />
        <span>度</span>
        <button type="button" className={styles.linkButton} onClick={() => onChange(0)} disabled={value === 0}>
          0に戻す
        </button>
      </div>
    </div>
  );
}

/**
 * 商品枠に入る画像 (§17)。特定の素材でなく「選択中の画像の何枚目か」を選ぶ。素材を差し替えると自動で新しい画像になる
 */
export function ProductImagePicker({
  element,
  assets,
  onChange,
}: {
  element: BannerLayoutElement;
  assets: InspectorAsset[];
  onChange: (next: BannerLayoutElement) => void;
}) {
  const current = productImageIndex(element, assets);
  return (
    <div className={styles.field} data-testid="banner-layout-product-image">
      <span>商品画像 (選択中の画像の何枚目か)</span>
      {assets.length === 0 ? (
        <span className={styles.inspectorHint}>生成時に選択中の画像が入ります。画像を選んでいないときは、この枠を外して生成します。</span>
      ) : (
        <>
          <div className={styles.assetGrid}>
            {assets.map((a, i) => (
              <button
                key={a.id}
                type="button"
                className={`${styles.assetOption} ${i === current ? styles.assetOptionActive : ''}`}
                onClick={() => onChange(withImageIndex(element, i))}
                title={`${i + 1}枚目${a.name ? `: ${a.name}` : ''}`}
                aria-pressed={i === current}
                aria-label={`${i + 1}枚目`}
              >
                <img src={a.thumbUrl} alt="" />
                <span className={styles.assetOptionIndex}>{i + 1}</span>
              </button>
            ))}
          </div>
          {current >= assets.length ? (
            <span className={styles.entryWarn}>{current + 1}枚目の画像が選ばれていないため、生成時にこの枠を外します</span>
          ) : (
            <span className={styles.inspectorHint}>素材を差し替えると、この枠の画像も自動で変わります。</span>
          )}
        </>
      )}
    </div>
  );
}
